/**
 * The TOC builder. Converts a list of documents, favorites and searchResults,
 * into a TOC that is usable by the documents/toc.jsx component
 */
import * as TocUtils from "./toc_utils";

export const MAX_SEARCH_RESULTS = 100;

/**
 * Builds a toc from a given documents array
 */
export function build(documents, favoritePageIds, searchResults = null, maxLevel = null) {
  var toc = [],
      matchingDocumentIds,
      favoriteRootToc,
      favorites = {},
      addedWhitepaperDivider = false,
      filteredSearchResults;

  // do we have any search results?
  if(searchResults) {
    // grab document IDs from search
    filteredSearchResults = _getCutOffSearchResults(searchResults);
    matchingDocumentIds = TocUtils.getMatchingDocumentIdsForSearchResults(documents, filteredSearchResults);
  }

  // do we have any favorite pages?
  if(favoritePageIds.length > 0) {
    // yes! so generate a virtual sub-toc
    favoriteRootToc = {
      id: "favorites",
      parentId: null,
      name: "Favorites",
      type: "favorite",
      favorite: true,
      matchCount: 0
    };

    toc.push(favoriteRootToc);
    favorites = _getFavoritesStructure(documents, favoritePageIds);
  }

  if(filteredSearchResults && matchingDocumentIds && matchingDocumentIds.length > 0) {
    // with search
    _buildTocWithSearch(documents, toc, filteredSearchResults, matchingDocumentIds);

  } else {
    // with our without search, that's the question
    _buildTocWithoutSearch(documents, toc);
  }

  // calculate depth of each section
  _setSectionDepthLevels(toc);

  // now we also try to do that for our favorites
  // we'll add pages for each favorite
  if(favoritePageIds.length > 0) {
    _buildFavoritesToc(
      toc,
      filteredSearchResults,
      matchingDocumentIds,
      favorites
    );
  }

  // filter out maxLevel
  if(maxLevel) toc = _filterTocOnMaxLevel(toc, maxLevel);

  return toc;
}

/**
 * Builds a hash with key as documentId and an array of pageNumbers that
 * are marked as favorite
 */
function _getFavoritesStructure(documents, favoritePageIds) {
  var structure = {};
  if(favoritePageIds.length == 0) return structure;

  // walk over all documents
  for(let document of documents) {
    let pageNumbers = [];

    // grab all page-ids of document
    let documentPageIds = document.pages.map(function(page) { return page.id });

    // walk over favorites
    for(let pageId of favoritePageIds) {
      // is favorite in page ids of doc?
      if(documentPageIds.indexOf(pageId) != -1) {
        // yes, so add page number to the list
        let page = TocUtils.getPage(document, pageId);
        pageNumbers.push(page.pageNumber);
      }
    }

    pageNumbers.sort();

    structure[document.id] = pageNumbers;
  }

  return structure;
}

/**
 * Tests if given section is in array of pagenumber hits
 */
function _getNumberOfMatchesInSection(pageNumbers, section) {
  var matches = 0;

  for(let pageNumber of pageNumbers) {
    if(pageNumber >= section.startPageNumber &&
       pageNumber <= section.endPageNumber)
       matches++;
  }

  return matches;
}

/**
 * Gives every section in toc a depth level
 */
function _setSectionDepthLevels(toc) {
  // now that we have the toc, we can determine what level each section has
  for(let section of toc) {
    section.level = TocUtils.getParentSectionIds(toc, section.id).length;
  }
}

/**
 * Removes sections that are higher than given maxLevel
 */
function _filterTocOnMaxLevel(toc, maxLevel) {
  let newToc = [];

  for(let section of toc) {
    if(section.level == null || section.level <= maxLevel)
      newToc.push(section);
  }

  return newToc;
}

/**
 * Builds TOC with given documents (without search results)
 */
function _buildTocWithoutSearch(documents, toc) {
  var addedWhitepaperDivider = false;

  // walk over documents
  for(let document of documents) {
    if(document.isWhitepaper && !addedWhitepaperDivider) {
      let whitepaperDividerToc = {
        id: `whitepapers`,
        parentId: null,
        name: "Whitepapers",
        divider: true
      };

      toc.push(whitepaperDividerToc);

      addedWhitepaperDivider = true;
    }

    // build new toc section
    // without parentId, since document is top-level
    let documentToc = {
      id: `document-${document.id}`,
      parentId: null,
      name: document.name,
      startPageNumber: 1,
      endPageNumber: document.pages.length,
      documentId: document.id,
      deepLink: true
    };

    let matchingPageNumbers;

    toc.push(documentToc);

    // ok pfew... we've done that, now walk over all chapters of document
    for(let section of document.sections) {
      // check if section matches
      // if not: SKIP
      let matchCount = 0,
          parentId;

      if(section.parentId) {
        parentId = `section-${section.parentId}`;
      } else {
        parentId = `document-${document.id}`;
      }

      // build new toc section but with parent of document
      let sectionToc = {
        id: `section-${section.id}`,
        documentId: document.id,
        parentId: parentId,
        startPageNumber: section.startPageNumber,
        endPageNumber: section.endPageNumber,
        name: section.name,
        matchCount: matchCount
      };

      toc.push(sectionToc);
    }
  }
}

/**
 * Builds up a toc with search results
 */
function _buildTocWithSearch(documents, toc, searchResults, matchingDocumentIds) {
  var index = 0;

  for(let document of documents) {
    if(matchingDocumentIds.indexOf(document.id) == -1) continue;

    // set levels
    _setSectionDepthLevels(document.sections);

    // retrieve the matching page numbers
    let pageNumbers = TocUtils.getPageNumbersFromSearchResults(searchResults, document.id);

    // walk over pages
    for(let pageNumber of pageNumbers) {
      let foundSection = null,
          maxLevel = -1;

      // walk over sections
      for(let section of document.sections) {
        // find match
        if(pageNumber >= section.startPageNumber &&
           pageNumber <= section.endPageNumber &&
           section.level > maxLevel) {
          maxLevel = section.level;
          foundSection = section;
        }
      }

      // did we find a section?
      if(foundSection) {
        // ok let's do it
        toc.push({
          id: `section-${foundSection.id}-${index}`,
          originalId: foundSection.id,
          documentId: document.id,
          parentId: null,
          startPageNumber: pageNumber,
          endPageNumber: pageNumber,
          name: `${foundSection.name} - page ${pageNumber}`,
          level: 1,
          type: null
        });
      } else {
        // somehow this doc has no sections, still push it
        toc.push({
          id: `section-doc-${document.id}-${index}`,
          originalId: null,
          documentId: document.id,
          parentId: null,
          startPageNumber: pageNumber,
          endPageNumber: pageNumber,
          name: `${document.name} - page ${pageNumber}`,
          level: 1,
          type: null
        });
      }

      index++;
    }
  }
}

/**
 * Adds page numbers under sub-sections for favoritized documents
 */
function _buildFavoritesToc(toc, searchResults, matchingDocumentIds, favorites) {
  var totalMatches = 0;

  // walk over favorites
  for(let documentId in favorites) {
    let pageNumbers = favorites[documentId],
        matchingPageNumbers;

    // no favorites for this document?
    // skip
    if(pageNumbers.length == 0) continue;

    // grab matching page numbers for doc
    if(matchingDocumentIds) {
      matchingPageNumbers = TocUtils.getPageNumbersFromSearchResults(
        searchResults,
        documentId
      );
    }

    // walk over page numbers that are our favorites
    for(let pageNumber of pageNumbers) {
      // if we have a search result, and results are not on these favorite pages
      // we are not interested in it
      if(matchingDocumentIds && matchingPageNumbers.indexOf(pageNumber) == -1)
        continue;

      let section = TocUtils.getSectionFromPage(toc, documentId, pageNumber);

      // section does not exist anymore?
      if(!section) continue;

      let name;

      if(matchingDocumentIds && matchingPageNumbers.length > 0) {
        name = section.name;
      } else {
        name = `${section.name} - page ${pageNumber}`;
      }

      let sectionToc = {
        id: `favorite-${section.id}-${pageNumber}`,
        documentId: documentId,
        parentId: "favorites",
        type: "favorite",
        startPageNumber: pageNumber,
        endPageNumber: pageNumber,
        name: name,
        matchCount: matchingDocumentIds ? 1 : 0,
        level: 2
      };

      if(matchingDocumentIds) totalMatches++;

      toc.push(sectionToc);
    }
  }

  // update match count of favorites
  if(totalMatches > 0) {
    let section = TocUtils.getSection(toc, "favorites");
    section.matchCount = totalMatches;
  }
}

/**
 * Returns new search results object with `hits` maxed to 100
 */
function _getCutOffSearchResults(searchResults) {
  var hits = [];

  for(let hit of searchResults.hits) {
    hits.push({
      pageNumber: hit.pageNumber,
      documentId: hit.documentId
    });

    if(hits.length >= MAX_SEARCH_RESULTS) break;
  }

  return {
    terms: searchResults.terms,
    hits: hits,
    matches: searchResults.matches
  };
}



// WEBPACK FOOTER //
// ./app/javascript/portal/documents/toc_builder.js